import React, { useEffect, useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  TrendingUp,
  Shield,
  Users,
  Building2,
  Landmark,
  CheckCircle2,
  DollarSign,
  BarChart3,
  Globe,
  Lock,
  ArrowRight,
  AlertTriangle,
  Info,
  Rocket,
  Target,
  Scale,
  Factory,
} from 'lucide-react';
import { economicSpecService } from '@/services/shared/economicSpecService';
import { QuickNavigation } from '../components/QuickNavigation';
import {
  generateAdoptionScenarios,
  generateROIScenarios,
  calculateBreakEvenAdoption,
  calculateSponsorshipCost,
  calculateFullMandateRevenue,
  calculateStatewideBreakEven,
  type CityRevenueInputs,
} from '../utils/revenueCalculations';

export interface CityPartnershipPageProps {
  cityName: string;
  stateName?: string;
  population?: number;
  annualConsumerSpending?: string;
  annualIndustrialSpending?: string;
  projectedCityRevenue?: string;
  projectedTreasuryRevenue?: string;
  residentCostSingleCity?: string;
  residentCostStatewide?: string;
  singleCityAnnualCost?: string;
  statewideAnnualCost?: string;
  processingRate?: string;
  cityRevenueSharePercent?: string;
  treasurySharePercent?: string;
  RevenueInfographComponent?: React.ComponentType;
  CityConsultationFormComponent?: React.ComponentType<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
  }>;
  potentialStatewidePartners?: string[];
}

/**
 * CityPartnershipPage - Parameterized municipal partnership page
 *
 * Explains city revenue model, legislative mandate opportunities, payment infrastructure,
 * and multi-city network benefits. Used to recruit city governments as partners.
 *
 * @example
 * ```tsx
 * <CityPartnershipPage
 *   cityName="Houston"
 *   stateName="Texas"
 *   population={2304580}
 *   annualConsumerSpending="$120B"
 *   projectedCityRevenue="$3M-$5M+"
 *   potentialStatewidePartners={['Dallas', 'Austin', 'San Antonio', 'Fort Worth']}
 *   CityConsultationFormComponent={HoustonCityForm}
 * />
 * ```
 */
export function CityPartnershipPage({
  cityName,
  stateName = '',
  population = 80000,
  annualConsumerSpending = '$6-8B',
  annualIndustrialSpending = '$19B',
  projectedCityRevenue = '$3M-$5M+',
  projectedTreasuryRevenue = '$3M-$5M+',
  residentCostSingleCity = '$0.60',
  residentCostStatewide = '$0.35',
  singleCityAnnualCost = '$576,000',
  statewideAnnualCost = '$336,000',
  // processingRate: Optional string for display - avoid hard-coded numerical promises unless approved
  processingRate = 'competitive with Stripe/Square (estimated)',
  cityRevenueSharePercent = '0.2%',
  treasurySharePercent = '0.2%',
  RevenueInfographComponent,
  CityConsultationFormComponent,
  potentialStatewidePartners = [],
}: CityPartnershipPageProps) {
  const [consultationOpen, setConsultationOpen] = useState(false);

  const cityStateDisplay = stateName ? `${cityName}, ${stateName}` : cityName;
  const cityPossessive = cityName.endsWith('s') ? `${cityName}'` : `${cityName}'s`;

  // Create a stable cityId for EconomicSpecService
  // In production, this would come from config (e.g., 'tenant-glastonbury')
  const cityId = useMemo(() => {
    if (stateName) {
      return `tenant-${stateName.toLowerCase()}-${cityName.toLowerCase()}`.replace(/\s+/g, '-');
    }
    return `tenant-${cityName.toLowerCase()}`.replace(/\s+/g, '-');
  }, [cityName, stateName]);

  // Get effective economic specs (config + runtime overrides via EconomicSpecService)
  // This enables the form system to override specs without code changes
  const effectiveSpecs = useMemo(() => {
    const baseConfig = {
      annualConsumerSpending: annualConsumerSpending || '$6-8B',
      annualIndustrialSpending,
      projectedCityRevenue: projectedCityRevenue || '$3M-$5M+',
      projectedCommunityTreasury: projectedTreasuryRevenue || '$3M-$5M+',
      dataSource: {
        consumerSpending: 'City Partnership Page Config',
      },
    };

    try {
      return economicSpecService.getEffectiveSpecs(cityId, baseConfig);
    } catch (error) {
      // If service fails, fall back to base config
      console.warn(`Failed to get effective specs for city ${cityId}:`, error);
      return baseConfig;
    }
  }, [
    cityId,
    annualConsumerSpending,
    annualIndustrialSpending,
    projectedCityRevenue,
    projectedTreasuryRevenue,
  ]);

  // Calculate all revenue scenarios dynamically based on EFFECTIVE specs (config + overrides)
  const revenueInputs: CityRevenueInputs = useMemo(
    () => ({
      population,
      annualConsumerSpending: effectiveSpecs.annualConsumerSpending,
      residentCostSingleCity,
      cityRevenueSharePercent,
      treasurySharePercent,
    }),
    [
      population,
      effectiveSpecs,
      residentCostSingleCity,
      cityRevenueSharePercent,
      treasurySharePercent,
    ],
  );

  const adoptionScenarios = useMemo(
    () => generateAdoptionScenarios(revenueInputs),
    [revenueInputs],
  );
  const roiScenarios = useMemo(() => generateROIScenarios(revenueInputs), [revenueInputs]);
  const breakEvenData = useMemo(() => calculateBreakEvenAdoption(revenueInputs), [revenueInputs]);
  const sponsorshipCostData = useMemo(
    () => calculateSponsorshipCost(population, residentCostSingleCity),
    [population, residentCostSingleCity],
  );
  const fullMandateData = useMemo(
    () => calculateFullMandateRevenue(revenueInputs),
    [revenueInputs],
  );
  const statewideBreakEvenData = useMemo(
    () =>
      residentCostStatewide
        ? calculateStatewideBreakEven(revenueInputs, residentCostStatewide)
        : null,
    [revenueInputs, residentCostStatewide],
  );

  // Calculate dynamic revenue projections from 30% adoption scenario (single source of truth)
  const moderateAdoptionScenario = adoptionScenarios.find((s) => s.adoptionRate === 0.3);
  const calculatedCityRevenue = moderateAdoptionScenario?.cityRevenue || projectedCityRevenue;
  const calculatedTreasuryRevenue =
    moderateAdoptionScenario?.treasuryRevenue || projectedTreasuryRevenue;

  // Scroll to top when page first loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Helper: parse spending range like '$200-300B' or '$6B'
  const parseSpendingRange = (spending?: string) => {
    if (!spending) return null;
    const match = spending.match(/\$(\d+)(?:-(\d+))?([BMK])?/i);
    if (!match) return null;
    const [, lowStr, highStr, unit] = match;
    const multiplier =
      unit === 'B' ? 1_000_000_000 : unit === 'M' ? 1_000_000 : unit === 'K' ? 1_000 : 1;
    const low = parseFloat(lowStr) * multiplier;
    const high = highStr ? parseFloat(highStr) * multiplier : low;
    return { low, high };
  };

  const parseRateDecimal = (rate?: string): number | null => {
    if (!rate) return null;
    const m = rate.match(/(~)?\s*(\d+(?:\.\d+)?)\s*%/);
    if (!m) return null;
    return parseFloat(m[2]) / 100;
  };

  const formatBigMoney = (val: number) => {
    if (val >= 1_000_000_000) return `$${(val / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
    if (val >= 1_000_000) return `$${Math.round(val / 1_000_000)}M`;
    if (val >= 1_000) return `$${Math.round(val / 1_000)}K`;
    return `$${Math.round(val)}`;
  };

  // Format range for display: if low === high, show single value; otherwise show range
  const formatMoneyRange = (low: number, high: number) => {
    // Use relative tolerance for large numbers to avoid floating point precision issues
    const tolerance = Math.max(1, Math.abs(low) * 0.0001); // 0.01% tolerance
    return Math.abs(low - high) < tolerance
      ? formatBigMoney(low)
      : `${formatBigMoney(low)} - ${formatBigMoney(high)}`;
  };

  const DEFAULT_PROCESSING_RATE = 0.029;
  const DEFAULT_TREASURY_SHARE = 0.002; // 0.2%
  const DEFAULT_ADOPTION = 0.3;
  const rateDecimal = parseRateDecimal(processingRate) ?? DEFAULT_PROCESSING_RATE;
  const spendingRange = parseSpendingRange(effectiveSpecs.annualConsumerSpending);
  const cityPlatformSpendingRange = spendingRange
    ? { low: spendingRange.low * DEFAULT_ADOPTION, high: spendingRange.high * DEFAULT_ADOPTION }
    : null;
  const fullPlatformSpendingRange = spendingRange
    ? { low: spendingRange.low, high: spendingRange.high }
    : null; // 100% participation baseline
  const cityFeeRange = cityPlatformSpendingRange
    ? {
        low: cityPlatformSpendingRange.low * rateDecimal,
        high: cityPlatformSpendingRange.high * rateDecimal,
      }
    : null;
  const cityProcessingFeesAtFullRange = fullPlatformSpendingRange
    ? {
        low: fullPlatformSpendingRange.low * rateDecimal,
        high: fullPlatformSpendingRange.high * rateDecimal,
      }
    : null;
  const cityTreasuryRange = cityPlatformSpendingRange
    ? {
        low: cityPlatformSpendingRange.low * DEFAULT_TREASURY_SHARE,
        high: cityPlatformSpendingRange.high * DEFAULT_TREASURY_SHARE,
      }
    : null;
  const cityTreasuryAtFullRange = fullPlatformSpendingRange
    ? {
        low: fullPlatformSpendingRange.low * DEFAULT_TREASURY_SHARE,
        high: fullPlatformSpendingRange.high * DEFAULT_TREASURY_SHARE,
      }
    : null;

  const processingRateLabel = (rate?: string) => {
    if (!rate) return 'competitive with Stripe/Square (estimated)';
    const isPercent = /\d+(?:\.\d+)?\s*%/.test(rate);
    if (isPercent) return rate.includes('(estimated)') ? rate : `${rate} (estimated)`;
    return rate;
  };

  return (
    <>
      {CityConsultationFormComponent && (
        <CityConsultationFormComponent open={consultationOpen} onOpenChange={setConsultationOpen} />
      )}

      <QuickNavigation />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/5 via-background to-primary/10 py-16 sm:py-20 md:py-24">
        <div className="container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-primary/10 text-primary border-primary/20">
              For City Officials & Economic Development
            </Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Generate Municipal Revenue Without Raising Taxes
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground mb-4 leading-relaxed max-w-3xl mx-auto">
              <span className="font-semibold text-primary">{calculatedCityRevenue}</span> potential
              municipal revenue (based on a 30% vendor adoption scenario). The city-wide consumer
              spending baseline is{' '}
              <span className="font-semibold">{annualConsumerSpending} (excl. heavy industry)</span>
              . Earn from transaction fees without raising taxes.
            </p>
            <p className="text-sm text-muted-foreground max-w-xl mx-auto mb-4">
              Independent splits: City share and Community Treasury share are separate (each scales
              with adoption). Platform keeps its own operating portion. No commingling.
            </p>
            {RevenueInfographComponent && <RevenueInfographComponent />}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
              <Button
                size="lg"
                className="text-base gap-2"
                onClick={() => setConsultationOpen(true)}
              >
                Schedule Consultation <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-base"
                onClick={() => {
                  const revenueSection = document.getElementById('revenue-model');
                  if (revenueSection) revenueSection.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                See the Numbers
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-primary/5 to-background">
        <div className="container px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">Two Partnership Models</h2>
              <p className="text-base text-muted-foreground max-w-3xl mx-auto">
                Choose <span className="font-semibold text-foreground">City-Sponsored Access</span>{' '}
                for predictable ROI and universal reach, or
                <span className="font-semibold text-foreground">
                  {' '}
                  Passive Transaction Model
                </span>{' '}
                with zero upfront cost.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {/* City-Sponsored Model */}
              <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-blue-600 text-white">Recommended</Badge>
                    <Rocket className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold">City-Sponsored Universal Access</h3>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">Annual Investment</p>
                    <p className="text-4xl font-bold text-blue-700">{singleCityAnnualCost}</p>
                  </div>
                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground mb-2">
                      Break-even at {breakEvenData.adoptionPercent} vendor adoption
                    </p>
                    <p className="text-sm text-muted-foreground mb-2">
                      30% adoption ={' '}
                      <span className="font-bold text-green-600">{calculatedCityRevenue}</span>{' '}
                      annual revenue
                    </p>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Universal resident access accelerates vendor adoption</span>
                  </div>
                </CardContent>
              </Card>

              {/* Passive Model */}
              <Card className="border-2 border-muted">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline">Alternative</Badge>
                    <DollarSign className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-bold">Passive Transaction Model</h3>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">Annual Investment</p>
                    <p className="text-4xl font-bold text-primary">$0</p>
                  </div>
                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground mb-2">
                      No upfront allocation required
                    </p>
                    <p className="text-sm text-muted-foreground mb-2">
                      Revenue scales with organic vendor adoption
                    </p>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span>Slower growth without universal resident access</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {cityFeeRange && cityTreasuryRange && (
              <div className="mt-6 text-center">
                <Card className="inline-block border-2 border-muted bg-background/50 p-4">
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-1">City-Wide Example (est.)</p>
                    <p className="text-lg font-semibold">
                      Processing {annualConsumerSpending} annually (excl. heavy industry)
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Platform processed volume (30% adoption):{' '}
                      {formatMoneyRange(
                        cityPlatformSpendingRange!.low,
                        cityPlatformSpendingRange!.high,
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Total processing fees (~{Math.round(rateDecimal * 100 * 10) / 10}% of
                      processed volume): {formatMoneyRange(cityFeeRange.low, cityFeeRange.high)}{' '}
                      (paid to payment processors)
                    </p>
                    <p className="text-sm mt-1 font-semibold text-primary">
                      City share (0.2% of processed volume):{' '}
                      {formatMoneyRange(cityTreasuryRange.low, cityTreasuryRange.high)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      At 100% participation, platform processed volume is{' '}
                      {formatMoneyRange(
                        fullPlatformSpendingRange!.low,
                        fullPlatformSpendingRange!.high,
                      )}{' '}
                      — City/Community share (0.2% each) becomes{' '}
                      {formatMoneyRange(
                        cityTreasuryAtFullRange!.low,
                        cityTreasuryAtFullRange!.high,
                      )}{' '}
                      per fund.
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Estimates assume 30% adoption for scenario math,{' '}
                      {Math.round(rateDecimal * 100 * 10) / 10}% processing rate, and a 0.2%
                      city/0.2% community treasury share. Processing fees are paid to payment
                      processors and are not retained by the city.
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Community Treasury */}
            <Card className="border-2 border-primary/20 bg-primary/5">
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className="flex-1 text-center sm:text-left">
                    <p className="text-sm text-muted-foreground mb-2">
                      Community Treasury (Independent Fund)
                    </p>
                    <p className="text-3xl sm:text-4xl font-bold text-primary">
                      {calculatedTreasuryRevenue}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Resident-controlled infrastructure fund at 30% adoption
                    </p>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Separate 0.2% revenue share flows to resident-controlled treasury for
                      community improvements. Scales independently alongside city revenue.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="mt-8 text-center">
              <Button
                variant="outline"
                onClick={() => {
                  const el = document.getElementById('city-paid-model');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                See Full ROI Analysis <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Model Section */}
      <section id="revenue-model" className="py-12 sm:py-16 mt-8">
        <div className="container px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-green-600/10 text-green-700 border-green-600/20">
                Revenue Model
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                Two Partnership Models, Same Revenue Structure
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
                Cities earn {cityRevenueSharePercent} of verified vendor commerce flowing through
                the platform. Higher vendor adoption = higher revenue. Revenue scales with actual
                transaction volume.
              </p>
              <p className="text-sm text-muted-foreground mt-3 max-w-xl mx-auto">
                <span className="font-semibold text-foreground">(1) Passive Model:</span> No upfront
                cost, slower growth, revenue-only when vendors transact.
                <span className="font-semibold text-foreground">(2) Sponsorship Model:</span> City
                funds infrastructure and promotes adoption via PR/business liaison outreach, faster
                ROI — with performance-based exit rights.
              </p>
            </div>

            {/* The Math */}
            <Card className="mb-12 border-2 border-primary/20 overflow-hidden">
              <div className="bg-gradient-to-r from-primary/10 to-accent/10 p-6">
                <h3 className="text-2xl font-bold text-center">How Cities Earn Revenue</h3>
                <p className="text-center text-sm text-muted-foreground mt-2">
                  Simple performance-based model
                </p>
              </div>
              <CardContent className="p-6 sm:p-8">
                {/* Mobile & Tablet: 2-column grid (hidden at lg breakpoint) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:hidden gap-6">
                  {/* Step 1 */}
                  <div className="text-center p-4 rounded-lg bg-primary/5">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary font-bold mb-2 text-sm">
                      1
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold text-primary mb-1">
                      {annualConsumerSpending}
                    </div>
                    <p className="text-xs font-semibold mb-1">{cityName} Consumer Spending</p>
                    <p className="text-[10px] text-muted-foreground">
                      Local commerce (excludes heavy industry)
                    </p>
                  </div>

                  {/* Step 2 */}
                  <div className="text-center p-4 rounded-lg bg-primary/5">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary font-bold mb-2 text-sm">
                      2
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold text-primary mb-1">30%</div>
                    <p className="text-xs font-semibold mb-1">Vendor Adoption</p>
                    <p className="text-[10px] text-muted-foreground">Verified payment network</p>
                  </div>

                  {/* Step 3 */}
                  <div className="text-center p-4 rounded-lg bg-primary/5">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary font-bold mb-2 text-sm">
                      3
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold text-primary mb-1">0.2%</div>
                    <p className="text-xs font-semibold mb-1">City Share</p>
                    <p className="text-[10px] text-muted-foreground">Transaction fee</p>
                  </div>

                  {/* Step 4 */}
                  <div className="text-center p-4 rounded-lg bg-green-600/5 border-2 border-green-600/20">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-green-600/10 text-green-600 font-bold mb-2 text-sm">
                      ✓
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold text-green-600 mb-1 break-words">
                      {calculatedCityRevenue}
                    </div>
                    <p className="text-xs font-semibold mb-1">Annual City Revenue</p>
                    <p className="text-[10px] text-muted-foreground">Municipal income</p>
                  </div>
                </div>

                {/* Desktop: 4-column flow with arrows (show at xl breakpoint) */}
                <div className="hidden xl:grid grid-cols-4 gap-6">
                  {/* Step 1 */}
                  <div className="relative">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary font-bold mb-3">
                        1
                      </div>
                      <div className="text-3xl font-bold text-primary mb-2">
                        {annualConsumerSpending}
                      </div>
                      <p className="text-sm font-semibold mb-1">{cityName} Consumer Spending</p>
                      <p className="text-xs text-muted-foreground">
                        Groceries, restaurants, services, local commerce
                      </p>
                    </div>
                    <div className="absolute top-1/2 -right-3 transform -translate-y-1/2">
                      <ArrowRight className="w-6 h-6 text-primary/30" />
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="relative">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary font-bold mb-3">
                        2
                      </div>
                      <div className="text-3xl font-bold text-primary mb-2">30%</div>
                      <p className="text-sm font-semibold mb-1">Vendor Adoption</p>
                      <p className="text-xs text-muted-foreground">
                        Flows through verified network
                      </p>
                    </div>
                    <div className="absolute top-1/2 -right-3 transform -translate-y-1/2">
                      <ArrowRight className="w-6 h-6 text-primary/30" />
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="relative">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary font-bold mb-3">
                        3
                      </div>
                      <div className="text-3xl font-bold text-primary mb-2">0.2%</div>
                      <p className="text-sm font-semibold mb-1">City Share</p>
                      <p className="text-xs text-muted-foreground">
                        Competitive with Stripe/Square
                      </p>
                    </div>
                    <div className="absolute top-1/2 -right-3 transform -translate-y-1/2">
                      <ArrowRight className="w-6 h-6 text-green-600/50" />
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div>
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-600/10 text-green-600 font-bold mb-3">
                        ✓
                      </div>
                      <div className="text-3xl font-bold text-green-600 mb-2">
                        {calculatedCityRevenue}
                      </div>
                      <p className="text-sm font-semibold mb-1">Annual City Revenue</p>
                      <p className="text-xs text-muted-foreground">Additional municipal income</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Revenue Scenarios */}
            <div className="mb-12">
              <h3 className="text-xl font-bold mb-6 text-center">
                Revenue Projections by Adoption Rate
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {adoptionScenarios.map((scenario, index) => {
                  const isHighlighted = scenario.adoptionRate === 0.3;
                  const isHigh = scenario.adoptionRate === 0.5;

                  return (
                    <Card
                      key={scenario.adoptionLabel}
                      className={
                        isHigh
                          ? 'border-2 border-green-600/30 bg-green-50'
                          : isHighlighted
                            ? 'border-2 border-primary/30 bg-primary/5'
                            : 'border-2 border-muted'
                      }
                    >
                      <CardContent className="pt-6">
                        <div className="text-center mb-4">
                          <div
                            className={`text-3xl font-bold mb-1 ${
                              isHigh
                                ? 'text-green-600'
                                : isHighlighted
                                  ? 'text-primary'
                                  : 'text-muted-foreground'
                            }`}
                          >
                            {scenario.adoptionLabel}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {scenario.adoptionDescription}
                          </p>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Transaction Volume:</span>
                            <span className="font-semibold">{scenario.transactionVolume}</span>
                          </div>
                          <div className="flex justify-between border-t pt-2">
                            <span className="text-muted-foreground">City Revenue:</span>
                            <span
                              className={`font-bold ${isHigh ? 'text-green-600' : 'text-primary'}`}
                            >
                              {scenario.cityRevenue}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Community Treasury:</span>
                            <span className="font-semibold">{scenario.treasuryRevenue}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* What City Can Do With Revenue */}
            <Card className="border-2 border-primary/20">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <Landmark className="w-10 h-10 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-3">
                      What Cities Can Fund with {calculatedCityRevenue}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                      This revenue can be earmarked for specific initiatives outside the general
                      fund, giving cities flexibility to invest in priorities that wouldn't
                      otherwise get funded.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-sm">Economic Development Initiatives</p>
                          <p className="text-xs text-muted-foreground">
                            Small business grants, workforce training
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-sm">Public Safety Enhancements</p>
                          <p className="text-xs text-muted-foreground">
                            Equipment upgrades, officer training
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-sm">Infrastructure Improvements</p>
                          <p className="text-xs text-muted-foreground">
                            Street repairs, drainage projects
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-sm">Parks & Recreation</p>
                          <p className="text-xs text-muted-foreground">
                            Facility upgrades, program expansion
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-sm">Digital Infrastructure</p>
                          <p className="text-xs text-muted-foreground">
                            Public Wi-Fi, smart city technology
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-sm">Community Programs</p>
                          <p className="text-xs text-muted-foreground">
                            Youth services, senior programs
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* City-Paid Universal Access Model */}
      <section
        id="city-paid-model"
        className="py-12 sm:py-16 bg-gradient-to-br from-blue-50 to-indigo-50"
      >
        <div className="container px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-blue-600/10 text-blue-700 border-blue-600/20">
                Universal Access Model
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                City-Sponsored Platform Access (Optional)
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
                Cities can sponsor platform access for all residents (funded through city budget),
                unlocking 100% adoption and maximizing transaction volume revenue while providing
                free community engagement infrastructure. City breaks even at ~8% vendor adoption.
              </p>
            </div>

            {/* How It Works */}
            <div className="bg-white rounded-2xl p-8 mb-8 border-2 border-blue-200">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Rocket className="w-6 h-6 text-blue-600" />
                How City-Paid Sponsorship Works
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3 text-blue-900">
                    What City Pays (Single-City Rate)
                  </h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold text-foreground">Flat-fee rate:</span>{' '}
                        {residentCostSingleCity}/resident/month ($
                        {(parseFloat(residentCostSingleCity.replace('$', '')) * 12).toFixed(2)}/year
                        per resident)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold text-foreground">{cityName} cost:</span>{' '}
                        {population.toLocaleString()} residents × $
                        {(parseFloat(residentCostSingleCity.replace('$', '')) * 12).toFixed(2)}/year
                        ={' '}
                        <span className="font-bold text-primary">{singleCityAnnualCost}/year</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold text-foreground">Annual prepayment:</span>{' '}
                        Flat fee, no usage tracking required
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold text-foreground">Budget certainty:</span>{' '}
                        Fixed cost every year (no surprises)
                      </span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-3 text-blue-900">What Residents Get</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold text-foreground">100% free access</span> to
                        community engagement platform
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold text-foreground">Group purchasing:</span>{' '}
                        Bulk discounts on services (solar, lawn care, etc.)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold text-foreground">Civic participation:</span>{' '}
                        Digital voting, proposal submissions
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold text-foreground">Community forums:</span>{' '}
                        Neighborhood discussions, event coordination
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* ROI Calculation */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 mb-8 border-2 border-green-300">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Target className="w-6 h-6 text-green-600" />
                Return on Investment: City Breaks Even at {breakEvenData.adoptionPercent} Vendor
                Adoption
              </h3>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                {breakEvenData.description}
              </p>
              <div className="space-y-4">
                {roiScenarios.map((scenario) => {
                  const isBreakEven = scenario.adoptionRate <= 0.1;
                  const borderColorMap: Record<string, string> = {
                    'bg-amber-50 border-amber-300': 'border-amber-400',
                    'bg-white border-green-200': 'border-green-300',
                    'bg-gradient-to-r from-green-100 to-emerald-100 border-green-400':
                      'border-green-400',
                    'bg-gradient-to-r from-emerald-100 to-green-200 border-emerald-500':
                      'border-emerald-500',
                  };
                  const borderClass = borderColorMap[scenario.cardColorClass] || 'border-green-300';

                  return (
                    <div
                      key={scenario.adoptionLabel}
                      className={`rounded-lg p-4 border-2 ${scenario.cardColorClass}`}
                    >
                      <p
                        className={`text-sm font-semibold mb-2 ${
                          isBreakEven ? 'text-amber-900' : 'text-green-900'
                        }`}
                      >
                        {scenario.scenarioName}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground mb-1">Transaction Volume</p>
                          <p className="font-bold text-lg text-foreground">
                            {scenario.transactionVolume}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground mb-1">
                            City Revenue ({cityRevenueSharePercent} share)
                          </p>
                          <p className="font-bold text-lg text-primary">{scenario.cityRevenue}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground mb-1">City Sponsorship Cost</p>
                          <p className="font-bold text-lg text-red-600">
                            -{scenario.citySponsorshipCost}
                          </p>
                        </div>
                      </div>
                      <div className={`mt-4 pt-4 border-t-2 ${borderClass}`}>
                        <p className="text-muted-foreground mb-1 text-xs">Net City Benefit</p>
                        <p
                          className={`font-bold text-2xl ${
                            isBreakEven ? 'text-amber-600' : 'text-green-600'
                          }`}
                        >
                          +{scenario.netCityBenefit}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {isBreakEven
                            ? '(City breaks even and starts generating surplus)'
                            : `(City makes ${scenario.roiMultiplier} return on sponsorship investment)`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Performance Partnership & Exit Rights */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-8 border-2 border-blue-300">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6 text-blue-600" />
                Performance Partnership: Shared Responsibility for Success
              </h3>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                Platform success requires collaboration between ToolboxLogic (infrastructure
                provider) and {cityName} (adoption driver via PR, business outreach, and
                departmental integration). This is a{' '}
                <span className="font-semibold text-foreground">3-year partnership model</span> with
                performance-based exit rights if adoption falls short.
              </p>

              {/* Timeline */}
              <div className="bg-white rounded-lg p-6 border-2 border-blue-200 mb-6">
                <h4 className="font-semibold mb-4 text-blue-900">Partnership Timeline</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-xl font-bold text-blue-600">Y1</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-foreground mb-1">
                        Infrastructure Buildout (6-12 months)
                      </p>
                      <p className="text-muted-foreground">
                        Platform development, city department integrations, champion elections,
                        vendor onboarding system setup. Payment: {singleCityAnnualCost} (50%
                        upfront, 50% on deployment-ready delivery).{' '}
                        <span className="font-semibold text-foreground">Non-refundable.</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                      <span className="text-xl font-bold text-green-600">Y2</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-foreground mb-1">
                        Live Deployment & First Year Operations
                      </p>
                      <p className="text-muted-foreground">
                        Platform goes live to all residents. City runs PR campaigns (mayor
                        announcements, business liaison outreach). Adoption tracking begins.
                        Payment: {singleCityAnnualCost} (paid quarterly or annually).{' '}
                        <span className="font-semibold text-foreground">Non-refundable.</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                      <span className="text-xl font-bold text-amber-600">Y3</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-foreground mb-1">
                        Validation Year & Exit Decision
                      </p>
                      <p className="text-muted-foreground">
                        24 months of live adoption data available. If vendor adoption remains below{' '}
                        {breakEvenData.adoptionPercent} break-even threshold, city can exit
                        partnership without penalty (no Year 4+ payments). If adoption meets/exceeds
                        targets, partnership continues at city's discretion.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-6 border-2 border-blue-200">
                  <h4 className="font-semibold mb-3 text-blue-900">
                    ToolboxLogic Responsibilities
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>
                        Build enterprise-grade platform infrastructure (payments, security,
                        compliance)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>
                        Integrate with city departments (live data feeds, API connections)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Provide easy vendor onboarding system (no technical barriers)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Train city liaisons and champion coordinators</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>
                        Quarterly adoption reports (vendor participation, transaction volume,
                        revenue tracking)
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-6 border-2 border-blue-200">
                  <h4 className="font-semibold mb-3 text-blue-900">{cityName} Responsibilities</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        PR campaigns (mayor endorsements, city communications, news coverage)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        Business liaison outreach (Chamber of Commerce partnerships, vendor
                        recruitment)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        Department integration support (assign liaisons, coordinate data access)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        Champion recruitment campaigns (leverage existing community networks)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        Optional: Mandate vendor participation via city ordinance (fastest path to
                        100% adoption)
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 bg-white rounded-lg p-6 border-2 border-green-200">
                <h4 className="font-semibold mb-3 text-green-900">Performance-Based Exit Rights</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div className="text-center p-3 bg-red-50 rounded border border-red-200">
                    <p className="font-semibold text-red-900 mb-1">Scenario 1: Below Break-Even</p>
                    <p className="text-xs text-muted-foreground">
                      After 24 months live, vendor adoption &lt; {breakEvenData.adoptionPercent} →
                      City can exit without penalty (no Year 4+ payments)
                    </p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded border border-amber-200">
                    <p className="font-semibold text-amber-900 mb-1">Scenario 2: Near Break-Even</p>
                    <p className="text-xs text-muted-foreground">
                      After 24 months live, adoption close but below target → City and platform
                      negotiate (extension, mandate strategy, or exit)
                    </p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded border border-green-200">
                    <p className="font-semibold text-green-900 mb-1">Scenario 3: Profitable</p>
                    <p className="text-xs text-muted-foreground">
                      After 24 months live, adoption ≥ {breakEvenData.adoptionPercent} → Partnership
                      continues indefinitely at city's discretion (annual renewal)
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-start gap-3 bg-amber-50 rounded-lg p-4 border border-amber-200">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <span className="font-semibold text-foreground">Legal note:</span> Total 3-year
                  investment: {singleCityAnnualCost} × 3 = $
                  {(parseFloat(singleCityAnnualCost.replace(/[^0-9.]/g, '')) * 3).toLocaleString()}.
                  Years 1-2 are non-refundable (infrastructure buildout + first year operations).
                  Exit rights apply Year 3+ if adoption falls below {breakEvenData.adoptionPercent}{' '}
                  threshold after 24 months of live deployment. Partnership terms subject to
                  contract negotiation and city council approval.
                </p>
              </div>
            </div>

            {/* Key Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-2 border-blue-200">
                <CardContent className="pt-6">
                  <Scale className="w-10 h-10 text-blue-600 mb-4" />
                  <h3 className="text-lg font-semibold mb-3">
                    Budget Offset from Community Treasury
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    Community Treasury also receives {treasurySharePercent} of transaction volume,
                    generating significant resident-controlled funds. This offsets city budget by
                    funding smaller infrastructure projects (parks, playgrounds, neighborhood
                    improvements) that would otherwise require city appropriations.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Residents fund local improvements via treasury, not city budget</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        City focuses on major infrastructure (roads, utilities, public safety)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Reduces citizen complaints about "small stuff" city ignores</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200">
                <CardContent className="pt-6">
                  <Users className="w-10 h-10 text-blue-600 mb-4" />
                  <h3 className="text-lg font-semibold mb-3">100% Adoption = Maximum Revenue</h3>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    When all residents have free access, city can encourage (or mandate) vendor
                    participation. Higher vendor adoption = more transaction volume = more revenue
                    for both city and community treasury.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        No "paywall" barrier - every resident can access vendor marketplace
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        Vendors see entire city as potential customer base (
                        {population >= 1000000
                          ? `${(population / 1000000).toFixed(1)}M`
                          : `${Math.round(population / 1000)}K`}{' '}
                        residents)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Network effects: More residents = more vendors = more commerce</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Multi-City Network Discount */}
            {potentialStatewidePartners.length > 0 && (
              <div className="mt-8 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-8 border-2 border-purple-300">
                <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <Globe className="w-6 h-6 text-purple-600" />
                  Even Better: Join a Statewide Network for Deeper Discounts
                </h3>
                <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                  If {cityName} partners with other {stateName} cities (
                  {potentialStatewidePartners.join(', ')}), the entire network qualifies for{' '}
                  <span className="font-bold text-foreground">
                    statewide network pricing: {residentCostStatewide}/resident/month
                  </span>
                  (
                  {Math.round(
                    ((parseFloat(residentCostSingleCity.replace('$', '')) -
                      parseFloat(residentCostStatewide.replace('$', ''))) /
                      parseFloat(residentCostSingleCity.replace('$', ''))) *
                      100,
                  )}
                  % cheaper than single-city {residentCostSingleCity}/month rate).
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg p-6 border-2 border-purple-200">
                    <h4 className="font-semibold mb-3 text-purple-900">
                      Single-City Rate (Current)
                    </h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex justify-between">
                        <span className="text-muted-foreground">Rate:</span>
                        <span className="font-semibold">
                          {residentCostSingleCity}/resident/month
                        </span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-muted-foreground">
                          Annual cost (
                          {population >= 1000000
                            ? `${(population / 1000000).toFixed(1)}M`
                            : `${Math.round(population / 1000)}K`}{' '}
                          residents):
                        </span>
                        <span className="font-bold text-lg text-primary">
                          {singleCityAnnualCost}/year
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-lg p-6 border-2 border-purple-400">
                    <h4 className="font-semibold mb-3 text-purple-900">Statewide Network Rate</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex justify-between">
                        <span className="text-muted-foreground">Rate:</span>
                        <span className="font-semibold text-green-700">
                          {residentCostStatewide}/resident/month (
                          {Math.round(
                            ((parseFloat(residentCostSingleCity.replace('$', '')) -
                              parseFloat(residentCostStatewide.replace('$', ''))) /
                              parseFloat(residentCostSingleCity.replace('$', ''))) *
                              100,
                          )}
                          % savings)
                        </span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-muted-foreground">
                          Annual cost (
                          {population >= 1000000
                            ? `${(population / 1000000).toFixed(1)}M`
                            : `${Math.round(population / 1000)}K`}{' '}
                          residents):
                        </span>
                        <span className="font-bold text-lg text-green-600">
                          {statewideAnnualCost}/year
                        </span>
                      </li>
                      <li className="flex justify-between pt-2 border-t">
                        <span className="text-muted-foreground">Annual savings:</span>
                        <span className="font-bold text-xl text-green-600">
                          $
                          {(
                            parseInt(singleCityAnnualCost.replace(/[$,]/g, '')) -
                            parseInt(statewideAnnualCost.replace(/[$,]/g, ''))
                          ).toLocaleString()}
                          /year
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
                {statewideBreakEvenData && (
                  <>
                    <div className="mt-6 bg-white rounded-lg p-4 border-2 border-purple-200">
                      <p className="text-sm font-semibold text-purple-900 mb-2">
                        Statewide Network Break-Even: {statewideBreakEvenData.adoptionPercent}{' '}
                        Vendor Adoption
                      </p>
                      <p className="text-xs text-muted-foreground mb-3">
                        With statewide pricing ({statewideBreakEvenData.cityCost} cost), city breaks
                        even at only {statewideBreakEvenData.adoptionPercent} vendor adoption (
                        {statewideBreakEvenData.transactionVolume} transaction volume ={' '}
                        {statewideBreakEvenData.cityRevenue} city revenue).
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                        <div className="text-center p-2 bg-purple-50 rounded">
                          <p className="text-xs text-muted-foreground mb-1">City Cost</p>
                          <p className="font-bold text-primary">
                            {statewideBreakEvenData.cityCost}
                          </p>
                        </div>
                        <div className="text-center p-2 bg-purple-50 rounded">
                          <p className="text-xs text-muted-foreground mb-1">Break-Even Adoption</p>
                          <p className="font-bold text-amber-600">
                            {statewideBreakEvenData.adoptionPercent}
                          </p>
                        </div>
                        <div className="text-center p-2 bg-green-50 rounded border border-green-200">
                          <p className="text-xs text-muted-foreground mb-1">At 30% Adoption</p>
                          <p className="font-bold text-green-600">
                            +{statewideBreakEvenData.profitAtModerate}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex items-start gap-3 bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                      <Info className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-indigo-800 leading-relaxed">
                        <span className="font-semibold">
                          Potential {stateName} Statewide Network:
                        </span>{' '}
                        {cityName}, {potentialStatewidePartners.join(', ')}. All cities benefit from
                        statewide commerce bridging and bulk payment processing rates.
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Legislative Mandate Benefits */}
      <section id="legislative-mandate" className="py-12 sm:py-16">
        <div className="container px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-purple-600/10 text-purple-700 border-purple-600/20">
                Advanced Strategy
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                Legislative Mandate: Unlocking Full Economic Potential
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
                Cities can pass local ordinances requiring payment network adoption, achieving 100%
                vendor participation and unlocking maximum revenue potential.
              </p>
            </div>

            {/* Small Commerce Mandate */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8 mb-8 border-2 border-indigo-300">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Building2 className="w-6 h-6 text-indigo-600" />
                Phase 1: Small Business & Local Commerce Mandate
              </h3>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                City Council passes ordinance requiring all local businesses (retail, restaurants,
                services) to process payments through approved payment network. This ensures 100% of
                local commerce ({annualConsumerSpending} annually) flows through platform,
                maximizing revenue for both city and community treasury.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="font-semibold mb-3 text-indigo-900">What Gets Mandated</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <span>Retail stores, grocery stores, gas stations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <span>Restaurants, bars, clubs, entertainment venues</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <span>Service providers (plumbers, HVAC, lawn care, salons)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <span>Local healthcare providers (dentists, urgent care)</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4 border-2 border-indigo-200">
                  <p className="text-sm font-semibold text-indigo-900 mb-3">
                    Revenue Impact (100% Small Business Adoption)
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Transaction Volume:</span>
                      <span className="font-semibold">{fullMandateData.transactionVolume}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <span className="text-muted-foreground">
                        City Revenue ({cityRevenueSharePercent}):
                      </span>
                      <span className="font-bold text-primary">{fullMandateData.cityRevenue}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Community Treasury ({treasurySharePercent}):
                      </span>
                      <span className="font-bold text-green-600">
                        {fullMandateData.treasuryRevenue}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <span className="text-muted-foreground">Combined Benefit:</span>
                      <span className="font-bold text-xl text-indigo-600">
                        {fullMandateData.combinedBenefit}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Phase 2: Industrial & B2B Commerce Expansion Section */}
      <section id="phase-2-expansion" className="py-12 sm:py-16">
        <div className="container px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-8 border-2 border-amber-300">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Factory className="w-6 h-6 text-amber-600" aria-hidden="true" />
                Phase 2: Industrial & B2B Commerce Expansion (Long-Term Vision)
              </h3>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                After small business mandate proves successful, city (or state legislature) can
                expand to include industrial/B2B transactions. {cityName} industrial economy
                processes{' '}
                <span className="font-bold text-foreground">
                  {annualIndustrialSpending} annually
                </span>{' '}
                (refineries, LNG terminals, petrochemical plants, port freight). Adding 0.2% fee to
                this volume generates transformational revenue for both city and community.
              </p>

              <div className="bg-white rounded-lg p-6 border-2 border-amber-400 mb-6">
                <p className="text-sm font-semibold text-amber-900 mb-4">
                  Full Economic Integration Scenario
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <p className="text-muted-foreground mb-1">Small Business Commerce</p>
                    <p className="font-bold text-lg">$6B/year</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1">Industrial B2B Commerce</p>
                    <p className="font-bold text-lg">$19B/year</p>
                  </div>
                </div>

                <div className="pt-4 border-t-2 border-amber-300">
                  <p className="text-xs text-muted-foreground mb-2">
                    Total Annual Transaction Volume
                  </p>
                  <p className="font-bold text-3xl text-amber-600 mb-4">$25-27B/year</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded p-3 border border-blue-200">
                      <p className="text-xs text-muted-foreground mb-1">
                        City Revenue (0.2% of $25-27B)
                      </p>
                      <p className="font-bold text-xl text-primary">$50-54M/year</p>
                    </div>
                    <div className="bg-green-50 rounded p-3 border border-green-200">
                      <p className="text-xs text-muted-foreground mb-1">
                        Community Treasury (0.2%)
                      </p>
                      <p className="font-bold text-xl text-green-600">$50-54M/year</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg text-card-foreground shadow-sm border-2 border-amber-400 bg-amber-50">
                <div className="p-6 pt-6">
                  <div className="flex items-start gap-3">
                    <Info
                      className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5"
                      aria-hidden="true"
                    />
                    <div>
                      <p className="font-semibold text-amber-900 mb-2">
                        Why Industrial Mandate Is Feasible
                      </p>
                      <ul className="space-y-2 text-sm text-amber-800">
                        <li className="flex items-start gap-2">
                          <span className="text-amber-600 font-bold">•</span>
                          <span>
                            <span className="font-semibold">Plants can't relocate:</span>{' '}
                            Refineries/LNG terminals are anchored infrastructure (can't move to
                            avoid 0.2% fee)
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-amber-600 font-bold">•</span>
                          <span>
                            <span className="font-semibold">Minimal cost impact:</span> 0.2% on{' '}
                            {annualIndustrialSpending} = rounding error for industrial contracts
                            (margins are much larger)
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-amber-600 font-bold">•</span>
                          <span>
                            <span className="font-semibold">Tax equivalent:</span> Treated as
                            payment processing fee, not additional sales tax (legally distinct)
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-amber-600 font-bold">•</span>
                          <span>
                            <span className="font-semibold">Community benefit:</span> $
                            {(() => {
                              const smallBusiness = parseInt(
                                fullMandateData.transactionVolume.replace(/[$B,]/g, ''),
                              );
                              const industrial = parseInt(
                                annualIndustrialSpending.replace(/[$B,]/g, ''),
                              );
                              const total = smallBusiness + industrial;
                              const revenue = Math.round(total * 0.002 * 100) / 100;
                              return `${revenue}-${Math.round(revenue * 1.08)}M`;
                            })()}
                            /year to community treasury transforms {cityName} infrastructure
                            capacity
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Infrastructure Section */}
      <section id="payment-infrastructure" className="py-12 sm:py-16 bg-muted/30">
        <div className="container px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-blue-600/10 text-blue-700 border-blue-600/20">
                Payment Infrastructure
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                Enterprise-Grade, City-Scale Payment Network
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
                Secure, compliant, and scalable payment infrastructure trusted by global platforms.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* Technology */}
              <Card className="border-2 border-primary/20">
                <CardContent className="pt-6">
                  <Shield className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-lg font-semibold mb-3">Enterprise Payment Processor</h3>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    Platform powered by enterprise payment infrastructure trusted by companies
                    processing
                    <span className="font-semibold text-foreground">
                      {' '}
                      hundreds of billions annually
                    </span>
                    . Same technology used by global marketplaces serving millions of users across
                    100+ countries.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <Lock className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold">PCI DSS Level 1 compliant</span> (highest
                        security standard)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Lock className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold">SOC 2 Type II certified</span> (audited
                        security controls)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        Trusted by <span className="font-semibold">Fortune 500 companies</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        Processes <span className="font-semibold">$100B+ annually</span> for
                        enterprise clients
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Legal Structure */}
              <Card className="border-2 border-primary/20">
                <CardContent className="pt-6">
                  <BarChart3 className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-lg font-semibold mb-3">Clean Legal Structure</h3>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    City receives revenue as a{' '}
                    <span className="font-semibold text-foreground">municipal service fee</span>,
                    not sales tax. Platform handles all payment compliance (PCI, fraud prevention,
                    dispute resolution).
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold">No money transmitter license</span> required
                        for city
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold">Clean separation:</span> Processor handles
                        funds, city receives share
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold">Automated revenue routing:</span> Split
                        settlements to city account
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        <span className="font-semibold">Full audit trail:</span> Quarterly reports
                        for municipal accounting
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Vendor Cost */}
            <Card className="border-2 border-green-600/20 bg-green-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <DollarSign className="w-10 h-10 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-green-900">
                      No Additional Cost to Vendors
                    </h3>
                    <p className="text-sm text-green-800 leading-relaxed">
                      Vendors pay{' '}
                      <span className="font-semibold">
                        competitive payment processing fees ({processingRateLabel(processingRate)}{' '}
                        range, similar to Stripe/Square)
                      </span>
                      . There is no "extra fee" on top of normal card processing costs. The city's
                      revenue share comes from the same transaction fee vendors already pay to
                      payment processors - we just route part of it locally instead of sending it
                      all to Silicon Valley payment companies.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Multi-City Scaling Section */}
      <section id="multi-city-scaling" className="py-12 sm:py-16">
        <div className="container px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-purple-600/10 text-purple-700 border-purple-600/20">
                Scalability
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                The More Cities Join, The Better It Gets
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
                As more cities adopt the platform, bulk transaction volume enables better payment
                processor rates, which means more savings for vendors and more revenue for cities.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Single City */}
              <Card className="border-2 border-muted">
                <CardHeader>
                  <Badge className="w-fit bg-muted text-foreground">Single City</Badge>
                  <h3 className="text-lg font-bold mt-2">{cityName} Alone</h3>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground">•</span>
                      <span>Transaction volume: {annualConsumerSpending}/year (30% adoption)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground">•</span>
                      <span>Vendor payment cost: ~3%</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground">•</span>
                      <span>City revenue: {calculatedCityRevenue}</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Multi-City Network */}
              <Card className="border-2 border-primary/30 bg-primary/5">
                <CardHeader>
                  <Badge className="w-fit bg-primary/20 text-primary">Multi-City Network</Badge>
                  <h3 className="text-lg font-bold mt-2">{stateName} (5 Cities)</h3>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        Combined annual commerce: $555-810B (Houston, Dallas, San Antonio, Austin,
                        Fort Worth)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Est. 30% adoption volume: ~$166-243B/year</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Vendor cost: ~2.95% (bulk pricing)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>City revenue increases OR vendor savings grow</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* National Network */}
              <Card className="border-2 border-green-600/30 bg-green-50">
                <CardHeader>
                  <Badge className="w-fit bg-green-600/20 text-green-700">National Network</Badge>
                  <h3 className="text-lg font-bold mt-2">100+ Cities</h3>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>
                        Combined annual commerce: $3-6T (top 100 US cities, excluding heavy
                        industry)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Est. 30% adoption volume: ~$900B-1.8T/year</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Vendor cost: ~2.9% (enterprise rates)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Maximum savings for all stakeholders</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
              <p className="text-sm text-blue-800 text-center leading-relaxed">
                <span className="font-semibold text-blue-900">Early-mover advantage:</span> Cities
                that join first benefit from lower implementation costs and get positioned as
                leaders in civic innovation. As the network scales, bulk volume negotiating power
                improves rates for everyone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-muted/30">
        <div className="container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-900 mb-2 text-sm">Important Disclosures</p>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    Revenue projections are illustrative estimates based on economic modeling of{' '}
                    {cityName} consumer spending data. Actual results depend on vendor adoption
                    rates, transaction volumes, payment processor contract terms, and economic
                    conditions. Payment processing infrastructure is provided by a third-party
                    enterprise processor - cities do not handle payment data or require money
                    transmitter licenses. Legal structure (municipal service fee vs other
                    classifications) requires review by city legal counsel and may vary by
                    jurisdiction. Implementation timeline and partnership terms subject to
                    negotiation and city council approval. This is not an offer or guarantee of
                    specific revenue amounts.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-16 sm:py-20 mt-12"
        style={{ background: 'linear-gradient(to bottom right, #1e3a8a, #172554, #020617)' }}
      >
        <div className="container px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
              Ready to Explore Partnership?
            </h2>
            <p className="text-lg sm:text-xl mb-8 text-white/90">
              Schedule a consultation to discuss how {cityName} can generate {calculatedCityRevenue}{' '}
              in performance-based revenue.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="text-base gap-2 !bg-white !text-primary hover:!bg-white/90 hover:!text-primary"
                onClick={() => setConsultationOpen(true)}
              >
                Schedule Consultation <ArrowRight className="w-4 h-4" />
              </Button>
              {/* <Button 
                size="lg" 
                className="text-base !bg-transparent !border !border-white/50 !text-white hover:!bg-white/20 hover:!border-white hover:!text-white"
                onClick={() => window.open('mailto:cities@toolboxlogic.com', '_blank')}
              >
                Email: cities@toolboxlogic.com
              </Button> */}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
